import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const switchVariants = cva(
  "relative inline-flex items-center shrink-0 cursor-pointer rounded-full transition-all duration-200",
  {
    variants: {
      size: {
        small: "w-[20px] h-[12px]",
        medium: "w-[28px] h-[16px]",
        large: "w-[36px] h-[20px]",
      },
      isChecked: {
        true: "bg-action-fill-primary-default ",
        false: "bg-surface-fill-neutral-subtle",
      },
      isDisabled: {
        true: "cursor-not-allowed opacity-60",
        false: "",
      },
    },
    compoundVariants: [
      {
        isChecked: true,
        isDisabled: false,
        class: "hover:bg-action-fill-primary-hover ",
      },
      {
        isChecked: false,
        isDisabled: false,
        class: "hover:bg-action-fill-neutral-faded",
      },
    ],
    defaultVariants: {
      size: "medium",
      isChecked: false,
      isDisabled: false,
    },
  }
);

const switchThumbVariants = cva(
  "inline-block rounded-full bg-white shadow-sm transition-transform duration-200",
  {
    variants: {
      size: {
        small: "h-[8px] w-[8px]",
        medium: "h-[12px] w-[12px]",
        large: "h-[16px] w-[16px]",
      },
      isChecked: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // Small size translations
      {
        size: "small",
        isChecked: false,
        class: "translate-x-[2px]",
      },
      {
        size: "small",
        isChecked: true,
        class: "translate-x-[10px]",
      },
      // Medium size translations
      {
        size: "medium",
        isChecked: false,
        class: "translate-x-[2px]",
      },
      {
        size: "medium",
        isChecked: true,
        class: "translate-x-[14px]",
      },
      // Large size translations
      {
        size: "large",
        isChecked: false,
        class: "translate-x-[2px]",
      },
      {
        size: "large",
        isChecked: true,
        class: "translate-x-[18px]",
      },
    ],
    defaultVariants: {
      size: "medium",
      isChecked: false,
    },
  }
);

export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /**
   * Label text to display next to the switch
   */
  label?: string;
  /**
   * Size of the switch
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether the switch is disabled
   */
  isDisabled?: boolean;
  /**
   * Custom class name for the container
   */
  containerClassName?: string;
  /**
   * Custom class name for the label
   */
  labelClassName?: string;
  /**
   * Custom class name for the switch track
   */
  trackClassName?: string;
  /**
   * Custom class name for the switch thumb
   */
  thumbClassName?: string;
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      label,
      size = "medium",
      isDisabled = false,
      containerClassName,
      labelClassName,
      trackClassName,
      thumbClassName,
      className,
      checked,
      onChange,
      ...props
    },
    ref
  ) => {
    const [internalChecked, setInternalChecked] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Use forwarded ref or internal ref
    React.useImperativeHandle(ref, () => inputRef.current!);

    const isChecked = checked !== undefined ? checked : internalChecked;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        setInternalChecked(e.target.checked);
      }
    };

    const handleContainerClick = () => {
      if (!isDisabled && inputRef.current) {
        inputRef.current.click();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === " " || e.key === "Enter") && !isDisabled) {
        e.preventDefault();
        inputRef.current?.click();
      }
    };

    // Size-based configurations
    const sizeConfig = {
      small: {
        gap: "gap-2",
        labelSize: "text-body-small-regular",
      },
      medium: {
        gap: "gap-2.5",
        labelSize: "text-body-small-regular",
      },
      large: {
        gap: "gap-3",
        labelSize: "text-body-medium-regular",
      },
    };

    const config = sizeConfig[size];

    return (
      <div className={cn("inline-flex flex-col", containerClassName)}>
        <div
          className={cn(
            "inline-flex items-center",
            config.gap,
            isDisabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          onClick={handleContainerClick}
          onKeyDown={handleKeyDown}
          role="switch"
          aria-checked={isChecked}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0}
        >
          <input
            ref={inputRef}
            type="checkbox"
            role="switch"
            className="sr-only"
            checked={isChecked}
            onChange={handleChange}
            disabled={isDisabled}
            {...props}
          />

          <div className="relative inline-flex shrink-0">
            <div
              className={cn(
                switchVariants({
                  size,
                  isChecked,
                  isDisabled,
                }),
                trackClassName,
                className,
                "focus-visible:ring-2 focus-visible:ring-action-outline-primary-faded focus-visible:ring-offset-2"
              )}
            >
              <span
                className={cn(
                  switchThumbVariants({
                    size,
                    isChecked,
                  }),
                  thumbClassName
                )}
              />
            </div>
          </div>

          {label && (
            <label
              className={cn(
                config.labelSize,
                "select-none inline-flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : "text-surface-ink-neutral-normal",
                labelClassName
              )}
            >
              {label}
            </label>
          )}
        </div>
      </div>
    );
  }
);

Switch.displayName = "Switch";

export { Switch, switchVariants };
